/*
 * SIMBA — ẢNH BẰNG CHỨNG
 * ============================================================================
 * Đọc, thu nhỏ và kiểm tra dung lượng ảnh học viên đính kèm bài nộp, trước khi
 * gửi lên dưới dạng data URL.
 */
export const MAX_EVIDENCE_IMAGE_BYTES = 200 * 1024;

export function estimateDataUrlBytes(dataUrl: string) {
  const base64 = String(dataUrl || "").split(",")[1] || "";
  return Math.floor((base64.length * 3) / 4);
}

export function localSubmissionUrlMessage(raw: string) {
  const value = String(raw || "").trim();
  if (!value) return "";
  if (/^[a-zA-Z]:[\\/]/.test(value) || value.startsWith("\\\\"))
    return "Link này nằm trong máy của con, Simba không mở được. Hãy gửi link public hoặc upload 1 ảnh bằng chứng.";
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return "Link chưa đúng định dạng. Hãy dùng link public bắt đầu bằng https:// hoặc upload 1 ảnh bằng chứng.";
  }
  const host = url.hostname.toLowerCase();
  if (!["http:", "https:"].includes(url.protocol))
    return "Simba chỉ nhận link public dạng http hoặc https.";
  if (
    host === "localhost" ||
    host.endsWith(".localhost") ||
    host.endsWith(".local") ||
    host === "::1" ||
    host === "0.0.0.0" ||
    host.startsWith("127.") ||
    host.startsWith("10.") ||
    host.startsWith("192.168.") ||
    host.startsWith("169.254.") ||
    /^172\.(1[6-9]|2\d|3[0-1])\./.test(host)
  )
    return "Link này là localhost/private network nên mentor và Simba không xem được. Hãy dùng link public hoặc upload 1 ảnh bằng chứng.";
  return "";
}

export function readFileAsDataUrl(file: File) {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(new Error("Không thể đọc ảnh."));
    reader.readAsDataURL(file);
  });
}

export function loadImage(dataUrl: string) {
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("Ảnh không hợp lệ."));
    image.src = dataUrl;
  });
}

export async function prepareEvidenceImage(file: File) {
  if (!/^image\/(png|jpe?g|webp)$/i.test(file.type))
    throw new Error("Chỉ nhận ảnh PNG, JPG hoặc WebP.");
  const original = await readFileAsDataUrl(file);
  if (estimateDataUrlBytes(original) <= MAX_EVIDENCE_IMAGE_BYTES) {
    return {
      data_url: original,
      file_name: file.name,
      mime_type: file.type,
      byte_size: estimateDataUrlBytes(original),
    };
  }
  const image = await loadImage(original);
  for (const maxSide of [1400, 1200, 1000, 800, 640]) {
    const scale = Math.min(1, maxSide / Math.max(image.width, image.height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(image.width * scale));
    canvas.height = Math.max(1, Math.round(image.height * scale));
    canvas.getContext("2d")?.drawImage(image, 0, 0, canvas.width, canvas.height);
    for (const quality of [0.82, 0.72, 0.62, 0.52, 0.44]) {
      const dataUrl = canvas.toDataURL("image/jpeg", quality);
      const byteSize = estimateDataUrlBytes(dataUrl);
      if (byteSize <= MAX_EVIDENCE_IMAGE_BYTES)
        return {
          data_url: dataUrl,
          file_name: file.name.replace(/\.[^.]+$/, ".jpg"),
          mime_type: "image/jpeg",
          byte_size: byteSize,
        };
    }
  }
  throw new Error("Ảnh quá lớn. Hãy crop hoặc chọn ảnh nhỏ hơn 200KB.");
}
